import { createRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import z from 'zod';
import { GeneralSpinner } from '@Components/generalSpinner';
import { getBusinessOwnersQueryDataOptions } from '@Pages/happyFlowPages/ownersCenter/api/ownersCenter.api';
import { AddUBO } from '@Pages/happyFlowPages/addUBO/AddUBO';
import { OwnersCenter } from '@Pages/happyFlowPages/ownersCenter/OwnersCenter';
import { beforeLoadGeneral } from '@Router/middleware';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import { getAllowedCountries } from '@Hooks/api/fetchCountries.api';
import { useBoundStore } from '@Store/index';
import { getBusinessOwnerData } from '@Pages/happyFlowPages/ownersCenter/api/util';
import { getBusinessOwnerQueryData } from '@Services/api/businessOwner';
import { rootRoute } from './rootRoute';

const AddOwnerRoute = createRoute({
  getParentRoute: () => rootRoute,
  validateSearch: zodValidator(
    z.object({
      uboId: z.string().optional(),
    })
  ),
  path: routesModel[RoutesMap.ADD_OWNER].path,
  beforeLoad: ({ search }) => {
    beforeLoadGeneral({ page: RoutesMap.ADD_OWNER, shouldUpdateServerNavStack: false });
    return {
      getBusinessOwnerQueryData: getBusinessOwnerQueryData(search.uboId),
    };
  },
  loaderDeps: ({ search }) => ({ uboId: search.uboId }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(getAllowedCountries),
      context.queryClient.ensureQueryData(context.getBusinessOwnerQueryData),
    ]);
  },
  component: AddUBO,
});

export const ownerCenterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.OWNER_CENTER].path,
  beforeLoad: () => {
    beforeLoadGeneral({ page: RoutesMap.OWNER_CENTER, shouldUpdateServerNavStack: true });
    const { businessExternalId, customerId } = getBusinessOwnerData();

    const isEmbedded = useBoundStore.getState().onboardingSlice.getIsEmbedded();

    const getBusinessOwnersQueryData = getBusinessOwnersQueryDataOptions({
      businessExternalId: businessExternalId!,
      isEmbedded,
      customerId: customerId!,
    });
    return {
      getBusinessOwnersQueryData,
    };
  },
  loader: async ({ context }) => {
    return context.queryClient.ensureQueryData(context.getBusinessOwnersQueryData);
  },
  component: OwnersCenter,
  wrapInSuspense: true,
  pendingComponent: () => <GeneralSpinner />,
});

export const ownerRoutes = [AddOwnerRoute, ownerCenterRoute];
